import {
  formatMarketTime,
  getMarketOpenAndCloseTickTimestamps,
  getSingleDayHourlyTickDates,
} from '@helpers';
import * as d3 from 'd3';
import { useEffect, useRef } from 'react';
import styles from './PriceChangeAxes.module.scss';

export type PriceChangesChartAxesProps = {
  xScale: d3.ScaleLinear<number, number>;
  yScale: d3.ScaleLinear<number, number>;
  yAxisFormatter: (n: number) => string;
  locale?: string;
  dateToIndexMap?: Map<number, number>;
};

export const PriceChangesChartAxes = ({
  xScale,
  yScale,
  yAxisFormatter,
  locale,
  dateToIndexMap,
}: PriceChangesChartAxesProps) => {
  const xAxisRef = useRef<SVGGElement>(null);
  const yAxisRef = useRef<SVGGElement>(null);

  useEffect(() => {
    if (xAxisRef.current && xScale.range()[1] > 0) {
      const axisSelection = d3.select(xAxisRef.current);

      // Index-based x-axis: domain is [0, N] where each index maps to a data point
      if (dateToIndexMap) {
        const indexToTime = new Map<number, number>();
        const marketOpenIndices: number[] = [];
        dateToIndexMap.forEach((index, timestamp) => {
          indexToTime.set(index, timestamp);
          const d = new Date(timestamp);
          if (d.getHours() === 9 && d.getMinutes() === 30) {
            marketOpenIndices.push(index);
          }
        });

        const [domainStart, domainEnd] = xScale.domain();

        // Use one tick per point for sparse datasets, otherwise 6 evenly spaced
        const pointCount = Math.floor(domainEnd - domainStart) + 1;
        const tickValues: number[] =
          pointCount <= 6
            ? Array.from({ length: pointCount }, (_, i) => domainStart + i)
            : Array.from({ length: 6 }, (_, i) =>
                Math.floor(domainStart + ((domainEnd - domainStart) / 5) * i)
              );

        // Snap the nearest tick to each market open index so 9:30 AM always appears
        const snapped = new Set<number>();
        for (const openIdx of marketOpenIndices) {
          let nearestPos = 0;
          let nearestDist = Infinity;
          for (let i = 0; i < tickValues.length; i++) {
            if (snapped.has(i)) continue;
            const dist = Math.abs(tickValues[i] - openIdx);
            if (dist < nearestDist) {
              nearestDist = dist;
              nearestPos = i;
            }
          }
          tickValues[nearestPos] = openIdx;
          snapped.add(nearestPos);
        }

        const formatterLinearChart = (domainValue: number, idx: number) => {
          const time = indexToTime.get(domainValue);
          if (!time) {
            return '';
          }

          const date = new Date(time);
          const minutes = date.getMinutes();

          if (minutes > 30 && idx !== tickValues.length - 1) {
            date.setHours(date.getHours() + 1);
          }

          const timeStr = formatMarketTime(date);

          const dateFormatter = d3.timeFormat('%b %d');
          const isMarketOpen = date.getHours() === 9 && minutes === 30;

          return isMarketOpen ? `${timeStr}|${dateFormatter(date)}` : timeStr;
        };

        axisSelection.call(
          d3
            .axisBottom<number>(xScale)
            .tickSize(0)
            .tickPadding(16)
            .tickValues(tickValues)
            .tickFormat(formatterLinearChart)
        );
      } else {
        // xScale domain is [minTimestamp, maxTimestamp] — place a tick at each whole hour
        const [domainMin, domainMax] = xScale.domain();
        const startDate = new Date(domainMin);
        const endDate = new Date(domainMax);
        const spanHours = (domainMax - domainMin) / (1000 * 60 * 60);

        if (spanHours > 24) {
          const tickValues = getMarketOpenAndCloseTickTimestamps(
            domainMin,
            domainMax
          );

          const lastTickValue = tickValues[tickValues.length - 1];
          const dateFormatter = d3.timeFormat('%b %d');

          axisSelection.call(
            d3
              .axisBottom<number>(xScale)
              .tickSize(0)
              .tickPadding(16)
              .tickValues(tickValues)
              .tickFormat((domainValue: number) => {
                const date = new Date(domainValue);
                const suffix = domainValue === lastTickValue ? ' ET' : '';
                const timeStr = formatMarketTime(date);
                const isOpen =
                  date.getHours() === 9 && date.getMinutes() === 30;
                return isOpen
                  ? `${timeStr}${suffix}|${dateFormatter(date)}`
                  : `${timeStr}${suffix}`;
              })
          );
        } else {
          // Single day: hourly ticks
          const tickValues = getSingleDayHourlyTickDates(
            startDate,
            endDate
          ).map(tickDate => tickDate.getTime());

          const lastTickValue = tickValues[tickValues.length - 1];

          axisSelection.call(
            d3
              .axisBottom<number>(xScale)
              .tickSize(0)
              .tickPadding(16)
              .tickValues(tickValues)
              .tickFormat((domainValue: number) => {
                const date = new Date(domainValue);
                const suffix = domainValue === lastTickValue ? ' ET' : '';
                return `${formatMarketTime(date)}${suffix}`;
              })
          );
        }
      }

      // Convert labels with "|" separator to multi-line labels
      axisSelection.selectAll('.tick text').each(function () {
        const text = d3.select(this);
        const content = text.text();

        if (content.includes('|')) {
          const [timeStr, dateStr] = content.split('|');
          text.text('');
          text.append('tspan').attr('x', 0).attr('dy', '0.71em').text(timeStr);
          text.append('tspan').attr('x', 0).attr('dy', '16px').text(dateStr);
        }
      });
    }

    if (yAxisRef.current && yScale.range()[0] > 0) {
      const axisSelection = d3.select(yAxisRef.current);
      const axis = d3
        .axisRight<number>(yScale)
        .ticks(4)
        .tickSize(xScale.range()[1])
        .tickFormat(yAxisFormatter)
        .tickPadding(10);

      axisSelection.call(axis).call(g => g.select('.domain').remove());
    }
  }, [xScale, yScale, locale, yAxisFormatter, dateToIndexMap]);

  return (
    <g>
      <g
        className={styles.chartAxisG}
        ref={xAxisRef}
        transform={`translate(0, ${yScale.range()[0]})`}
        data-testid="chart-x-axis-group"
      />
      <g className={styles.chartAxisG} ref={yAxisRef} />
    </g>
  );
};
